import { forwardRef, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "@/lib/apiClient";
import { SelectPopover, type SearchableOption } from "@/components/SearchableSelect/SearchableSelect";

export interface AsyncSearchPage {
  options: SearchableOption[];
  hasNextPage: boolean;
  totalCount: number;
}

export type AsyncSearchFn = (args: {
  keyword: string;
  pageNumber: number;
}) => Promise<AsyncSearchPage>;

export type AsyncResolveFn = (value: string) => Promise<SearchableOption | null>;

const DEBOUNCE_MS = 300;

function dedupe(options: SearchableOption[]): SearchableOption[] {
  const seen = new Set<string>();
  return options.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
}

function errorText(e: unknown): string {
  if (e instanceof ApiError && e.status === 403) return "You don't have permission to view this list.";
  return e instanceof Error ? e.message : "Failed to load options.";
}

export const AsyncSearchableSelect = forwardRef<HTMLButtonElement, {
  value: string;
  onValueChange: (value: string) => void;
  search: AsyncSearchFn;
  resolveValue?: AsyncResolveFn;
  initialOption?: SearchableOption | null;
  pinnedOptions?: SearchableOption[];
  exclude?: string[];
  onValueMissing?: () => void;
  onError?: (error: unknown) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  id?: string;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
  contentClassName?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}>(function AsyncSearchableSelect({
  value,
  onValueChange,
  search,
  resolveValue,
  initialOption = null,
  pinnedOptions,
  exclude,
  onValueMissing,
  onError,
  placeholder = "Select…",
  emptyText = "No matches.",
  ...rest
}, ref) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SearchableOption[]>([]);
  const [selected, setSelected] = useState<SearchableOption | null>(initialOption);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchFn = useRef(search);
  searchFn.current = search;
  const resolveFn = useRef(resolveValue);
  resolveFn.current = resolveValue;
  const missingFn = useRef(onValueMissing);
  missingFn.current = onValueMissing;
  const errorFn = useRef(onError);
  errorFn.current = onError;

  const currentValue = useRef(value);
  currentValue.current = value;

  const requestId = useRef(0);
  const loadedKeyword = useRef<string | null>(null);
  const opened = useRef(false);
  const resolvedFor = useRef<string | null>(null);

  const load = useCallback(async (keyword: string, page: number) => {
    const id = requestId.current + 1;
    requestId.current = id;
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await searchFn.current({ keyword, pageNumber: page });
      if (id !== requestId.current) return;
      setItems((prev) => (page === 1 ? res.options : dedupe([...prev, ...res.options])));
      setHasNextPage(res.hasNextPage);
      setTotalCount(res.totalCount);
      setPageNumber(page);
      setError(null);
    } catch (e) {
      if (id !== requestId.current) return;
      if (page === 1) setItems([]);
      setHasNextPage(false);
      setError(errorText(e));
      errorFn.current?.(e);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!value) {
      resolvedFor.current = null;
      setSelected(null);
      return;
    }
    if (selected?.value === value) return;

    const pinned = pinnedOptions?.find((o) => o.value === value);
    if (pinned) {
      setSelected(pinned);
      return;
    }

    const hit = items.find((o) => o.value === value);
    if (hit) {
      setSelected(hit);
      return;
    }
    if (!resolveFn.current) return;
    if (resolvedFor.current === value) return;
    resolvedFor.current = value;

    const requested = value;
    const isCurrent = () => currentValue.current === requested;
    setResolving(true);
    resolveFn.current(requested)
      .then((option) => {
        if (!isCurrent()) return;
        if (option) setSelected(option);
        else missingFn.current?.();
      })
      .catch((e) => {
        if (!isCurrent()) return;
        errorFn.current?.(e);
        missingFn.current?.();
      })
      .finally(() => {
        if (isCurrent()) setResolving(false);
      });
  }, [value, items, selected, pinnedOptions]);

  function onOpenChange(open: boolean) {
    if (!open) return;
    if (opened.current) return;
    opened.current = true;
    loadedKeyword.current = "";
    void load("", 1);
  }

  useEffect(() => {
    if (!opened.current) return;
    if (query === loadedKeyword.current) return;
    const timer = setTimeout(() => {
      loadedKeyword.current = query;
      void load(query, 1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, load]);

  function onScrollEnd() {
    if (loading || loadingMore || !hasNextPage) return;
    void load(loadedKeyword.current ?? "", pageNumber + 1);
  }

  const q = query.trim().toLowerCase();
  const results = exclude?.length ? items.filter((o) => !exclude.includes(o.value)) : items;
  const pinned = (pinnedOptions ?? []).filter(
    (o) => !q || `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(q),
  );
  const showSelected =
    selected &&
    !q &&
    !results.some((o) => o.value === selected.value) &&
    !pinned.some((o) => o.value === selected.value);
  const visible = dedupe([
    ...pinned,
    ...(showSelected && selected ? [selected] : []),
    ...results,
  ]);

  const footer = error && results.length > 0
    ? <span className="text-destructive">{error}</span>
    : totalCount > results.length
      ? `Showing ${results.length} of ${totalCount} — type to search the rest.`
      : null;

  return (
    <SelectPopover
      {...rest}
      ref={ref}
      value={value}
      onValueChange={onValueChange}
      selected={selected}
      items={visible}
      showSearch
      query={query}
      onQueryChange={setQuery}
      onOpenChange={onOpenChange}
      loading={loading || resolving}
      loadingMore={loadingMore}
      footer={footer}
      onScrollEnd={onScrollEnd}
      placeholder={resolving ? "Loading…" : placeholder}
      emptyText={error ?? emptyText}
    />
  );
});
